import {
  type FullAssessmentSectionId,
  type FullAssessmentSectionResult,
  FullAssessmentSectionStatus,
} from '@azymmetric/shared/types/full-assessment.types';
import { useLlmSuggestionResolver } from '@components/shared/llm-chat/llm-suggestion-resolver.hook';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Per-section accept/reject telemetry for Full Assessment drafts: every
 * section's draft chain resolves per version logId — a new draft supersedes
 * (rejects) the section's prior version, accept resolves the accepted version,
 * and closing with unaccepted drafts rejects each of them. Built on the shared
 * `useLlmSuggestionResolver` core (dedup + mutation + Sentry).
 * See docs/iterations/AZ-393/AZ-393.DECISIONS.md (D7).
 */
export const useFullAssessmentResolutionLog = (results: Map<FullAssessmentSectionId, FullAssessmentSectionResult>) => {
  const { resetResolved, resolveLog } = useLlmSuggestionResolver();

  const lastLogIdsRef = useRef<Map<FullAssessmentSectionId, string>>(new Map());

  // Supersession: a section's new version rejects its previous one.
  useEffect(() => {
    for (const [sectionId, result] of results) {
      if (!result.logId) continue;

      const previousLogId = lastLogIdsRef.current.get(sectionId);
      if (previousLogId && previousLogId !== result.logId) {
        resolveLog(previousLogId, 'reject');
      }

      lastLogIdsRef.current.set(sectionId, result.logId);
    }
  }, [results, resolveLog]);

  /**
   * Reject every completed-but-unaccepted draft (close/discard path).
   */
  const rejectUnaccepted = useCallback(
    (params: { acceptedLogIds: Set<string>; results: Map<FullAssessmentSectionId, FullAssessmentSectionResult> }) => {
      for (const result of params.results.values()) {
        if (
          result.logId &&
          result.status === FullAssessmentSectionStatus.COMPLETED &&
          !params.acceptedLogIds.has(result.logId)
        ) {
          resolveLog(result.logId, 'reject');
        }
      }
    },
    [resolveLog],
  );

  const reset = useCallback(() => {
    lastLogIdsRef.current = new Map();
    resetResolved();
  }, [resetResolved]);

  return { rejectUnaccepted, reset, resolveLog };
};
